import * as PIXI from 'pixi.js';
import type { UpdateFlowMixinSettings } from '../core/updateFlowMixin';
import { UpdateFlowMixin } from '../core/updateFlowMixin';
import { assertMinimum, assertNumber, assertNumberBetween } from '../validators';
import { Config } from '../video-data/config';

export type TimelineSettings = UpdateFlowMixinSettings & {
    name?: string;
    duration?: number;
};

export type TimeSlice = [number, number];

type Callback = {
    function: Function;
    scope: any;
    params: any;
    emitted: boolean;
    time: number;
};

export const TIMELINE_EVENT_PRECISION = 0.1;

/**
 * Base timeline class.
 *
 * This class should not be used directly, but extend to create a new timeline type.
 * This extended class will control the placement of child timelines.
 * Ex. sequential timeline places child timelines one after the other while concurrent timeline allows placements anywhere.
 *
 * Currently available Timeline classes:
 * - SequentialTimeline
 * - ConcurrentTimeline
 *
 * A timeline represents an amount of time and gives us control over when certain events can or should happen.
 * It does that by defining a start, duration and local time.
 * When the local time is in between the start and end time (start + duration), the timeline is considered to be active and our progress will have a value between 0 and 1.
 * A value of 0 equals the start, while the 1 indicates that we're at the end of the active timeline.
 * This value or progress can then be passed to an AnimationCurveManager to drive an object's properties.
 */
export abstract class BaseTimeline extends UpdateFlowMixin<{
    new (settings?: unknown): PIXI.utils.EventEmitter;
}>(PIXI.utils.EventEmitter) {
    override _classType: string = Config.BASE_TIMELINE_TYPE;

    name: string = 'BaseTimeline';

    _emittedStart = false;
    _emittedEnd = false;
    #destroyed = false;

    children: BaseTimeline[] = [];

    callbacks: Callback[] = [];

    #parent: BaseTimeline = null;

    /**
     * Indicates if the current play head of a timeline hierarchy falls within the current timeline.
     * Once the play head reaches the very start of the timeline it becomes active. Once the
     * play head reaches the time equal to the timeline's duration then it becomes inactive.
     * Note that the playing state does NOT influence active. If you want to see if a timeline
     * is playing and active you need to check both.
     */
    #active = false;

    /**
     * Duration of this timeline.
     */
    #duration = 0;

    /**
     * Start time relative to the parent.
     */
    _relativeStartTime = 0;

    /**
     * Current time for this timeline. Can be negative if the play head for the timeline
     * hierarchy is before the start of this timeline.
     */
    #currentTime = 0;

    constructor(settings: TimelineSettings = {}) {
        super({
            ...settings,
            updateParentOnUpdate: true,
        });

        if (settings.hasOwnProperty('name')) {
            this.name = settings.name;
        }

        if (settings.hasOwnProperty('duration')) {
            assertNumber(settings.duration);
            this.#duration = settings.duration;
        }
    }

    get parent(): BaseTimeline {
        return this.#parent;
    }

    set parent(value: BaseTimeline) {
        if (this.#parent === value) {
            return;
        }
        this.#parent = value;
        this.requiresUpdate = true;
    }

    getChildIndex(child: BaseTimeline): number {
        const index = this.children.indexOf(child);

        if (index === -1) {
            throw new Error('The supplied timeline is not a child');
        }

        return index;
    }

    get active(): boolean {
        return this.#active;
    }

    get duration(): number {
        return this.#duration;
    }

    /**
     * Set the duration of the timeline.
     * This is only possible if the timeline has no children.
     * If timeline has children, duration is determined from its children.
     * Note: This will trigger the update loop.
     */
    set duration(value: number) {
        // Disable setting the duration if we have any children for the now.
        // We could extend this in the future to where scaling the parent, scales the children linearly.
        if (this.children.length > 0) {
            throw new Error(
                `Unable to set duration on ${this.name} as it is derived from its children.`,
            );
        }
        if (value === this.duration) {
            return;
        }
        assertMinimum(value, 0);
        this.#duration = value;
        this.requiresUpdate = true;
    }

    /**
     * Start time relative to the parent. If there's no parent this is undefined
     */
    get relativeStartTime(): number | undefined {
        if (!this.parent) {
            return undefined;
        }
        return this._relativeStartTime;
    }

    /**
     * Gets the start time relative to an ancestor of this timeline.
     */
    startTimeRelativeToAncestor(ancestor: BaseTimeline): number {
        let currentTimeline: BaseTimeline = this;
        let accumulatedOffset = 0;
        while (currentTimeline && currentTimeline !== ancestor) {
            accumulatedOffset += currentTimeline.relativeStartTime;
            currentTimeline = currentTimeline.parent;
        }
        if (!currentTimeline) {
            throw new Error(`Timeline ${this.name}, is not a descendent of ${ancestor.name}`);
        }
        return accumulatedOffset;
    }

    /**
     * End time relative to the parent. If there's no parent this is undefined
     */
    get relativeEndTime(): number | undefined {
        if (!this.parent) {
            return undefined;
        }
        return this.relativeStartTime + this.duration;
    }

    /**
     * Current time of this timeline relative to its own start.
     *
     * Setting the current time will automatically propagate the time to all nested
     * timelines. Note that this does not propagate time upwards (to any parents). Using this
     * setter also doesn't trigger an update for timelines (the update loop doesn't run).
     */
    get currentTime() {
        return this.#currentTime;
    }

    set currentTime(currentTime) {
        this.#currentTime = currentTime;
        this.#active = this.#isActiveAt(currentTime);

        this.#propagateCurrentTime();
        this.#resetEvents();
        this.#emitEventsAndRunCallbacks();
    }

    #propagateCurrentTime() {
        this.children.forEach((child) => {
            child.currentTime = this.#currentTime - child.relativeStartTime;
        });
    }

    #resetEvents() {
        if (this.#currentTime > this.duration && this._emittedEnd) {
            return;
        }
        if (this.#currentTime < 0) {
            this._emittedStart = false;
            this._emittedEnd = false;
        } else if (this.#currentTime < this.duration && this.#currentTime > 0) {
            this._emittedEnd = false;
        } else if (this.#currentTime === 0) {
            this._emittedStart = false;
            this._emittedEnd = false;
        }
    }

    #emitEventsAndRunCallbacks() {
        // trigger events if necessary
        // Use ceiling time to 1 decimal place for emitting events
        const roundedTime =
            Math.ceil(this.#currentTime / TIMELINE_EVENT_PRECISION) * TIMELINE_EVENT_PRECISION;

        let isPastStartButNotEmittedStartEvent =
            roundedTime >= 0 && !this._emittedStart && this.playing();
        let isPastEndButNotEmittedEndEvent =
            roundedTime >= this.duration && !this._emittedEnd && this.playing();

        // sometimes the timeline will start right at the end of a scene, so the time is past the start and the end
        // and neither the 'start' nor 'end' events have been emitted. In this case we previously would emit
        // the two events in quick succession. That wasn't great for some of our async stuff (like audio playback)
        // which would sometimes not stop!
        // So if we are at the end already, don't bother emitting the 'start' OR 'end' events.
        if (isPastStartButNotEmittedStartEvent && isPastEndButNotEmittedEndEvent) {
            isPastStartButNotEmittedStartEvent = false;
            isPastEndButNotEmittedEndEvent = false;
        }

        // emit an event at the start of this timeline. This can then be used to
        // trigger the playback of media elements like audio or video.
        if (isPastStartButNotEmittedStartEvent) {
            this.emit('start');
            this._emittedStart = true;
        }

        // run any callbacks for the current time
        this.#runCallbacks(roundedTime);

        // emit an event at the end of this timeline. This can then be used to
        // stop the playback of media elements like audio or video.
        if (isPastEndButNotEmittedEndEvent) {
            this.emit('end');
            this._emittedEnd = true;
        }
    }

    /**
     * Move the child timeline at position `oldIndex` to position `newIndex`.
     */
    moveChild(oldIndex: number, newIndex: number) {
        const minIndex = 0;
        const maxIndex = this.children.length - 1;
        assertNumberBetween(oldIndex, minIndex, maxIndex);
        assertNumberBetween(newIndex, minIndex, maxIndex);

        const child = this.children[oldIndex];
        child._moveFromTo(this, oldIndex, newIndex);

        // for the base implementation, re-ordering children does not
        // require an update for the hierarchy
    }

    _moveFromTo(parent: BaseTimeline, oldIndex: number, newIndex) {
        parent.children.splice(oldIndex, 1);
        parent.children.splice(newIndex, 0, this);
    }

    /**
     * Determines whether the Timeline has any children
     */
    get hasChildren(): boolean {
        return this.children.length !== 0;
    }

    /**
     * NOTE: "playing" means ANY timeline in the tree is playing (it's a "global" state). If you want to know
     *       if this specific timeline is currently actively playing, check `playing()` AND `Timeline.active`
     * @returns True if the Timeline is playing
     */
    playing(): boolean {
        if (this.parent) {
            return this.parent.playing();
        }
        return false;
    }

    /**
     * Start playback
     */
    play() {
        if (this.parent) {
            this.parent.play();
        }
    }

    /**
     * Used to set autoUpdate only on this timeline, without propagating to children.
     * Can be used if we know changes don't impact child timelines.
     */
    #setAutoUpdateShallow(value: boolean) {
        super.autoUpdate = value;
    }

    override set autoUpdate(value) {
        if (this.autoUpdate === value) {
            return;
        }
        // order is important here, this updates timelines from bottom up,
        // which is how they need to be calculated
        this.children.forEach((child) => {
            child.autoUpdate = value;
        });
        super.autoUpdate = value;
    }

    override get autoUpdate() {
        return super.autoUpdate;
    }

    override _update() {
        this.#updateDuration();
    }

    #updateDuration() {
        if (!this.hasChildren) {
            return;
        }
        this.#duration = Math.max(...this.children.map((child) => child.relativeEndTime));
    }

    /**
     * Adds a Timeline as a child. Once a timeline has a child, it's duration can no longer be
     * set, but rather it is derived from its children.
     *
     * Note that children must be of the same classType for a given timeline.
     *
     * @param childTimeline - the Timeline to add
     * @param index - the index within the parent's children
     * @param grow - controls if the parent's duration can increase due to the new child
     */
    add(childTimeline: BaseTimeline, index?: number, grow = true) {
        const initAutoUpdate = this.autoUpdate;
        const initDuration = this.duration;
        this.#setAutoUpdateShallow(false);
        childTimeline.autoUpdate = false;

        if (this.children.length > 0 && this.children[0]._classType !== childTimeline._classType) {
            throw new Error('Sibling timelines must be of the same class type');
        }

        // add childTimeline to our children
        if (index === undefined) {
            index = this.children.length;
        }
        this.children.splice(index, 0, childTimeline);
        childTimeline.parent = this;

        // if we don't want the total duration to grow when adding this timeline,
        // then we have to remove the difference from the other children.
        if (!grow) {
            if (index === 0) {
                this.children[0].duration = initDuration;
            } else {
                const prevChild = this.children[index - 1];
                prevChild.duration = prevChild.duration - childTimeline.duration;
            }
        }

        this.requiresUpdate = true;
        childTimeline.autoUpdate = initAutoUpdate;
        this.#setAutoUpdateShallow(initAutoUpdate);

        // After the timeline is added and updated, we need to sync the current time
        childTimeline.currentTime = this.currentTime - childTimeline.relativeStartTime;
    }

    /**
     * Remove the given Timeline as a child.
     * @param childTimeline - the timeline we want to parent
     */
    remove(childTimeline: BaseTimeline) {
        const initAutoUpdate = this.autoUpdate;
        this.autoUpdate = false;
        const childIndex = this.getChildIndex(childTimeline);
        childTimeline._removeFromParent(this, childIndex);

        // if we have no more children, use previous duration value
        if (!this.hasChildren) {
            this.duration = childTimeline.duration;
        }

        this.requiresUpdate = true;
        this.autoUpdate = initAutoUpdate;
    }

    _removeFromParent(parent: BaseTimeline, index: number) {
        parent.children.splice(index, 1);
        this.parent = null;
    }

    #isActiveAt(currentTime: number) {
        return currentTime >= 0 && currentTime < this.duration;
    }

    /**
     * Get a list of children indices which are active at the given relative time
     * @returns a list of child indices which indicate the active children
     */
    getActiveChildrenIndicesAt(currentTime: number): number[] {
        const activeIndices = [];
        this.children.forEach((child, index) => {
            const childTime = currentTime - child.relativeStartTime;
            if (child.#isActiveAt(childTime)) {
                activeIndices.push(index);
            }
        });

        return activeIndices;
    }

    /** Returns a progress value representing the mid-point for each child timeline */
    getProgressMidPointForChildren(): Array<number> {
        if (this.children.length === 0) {
            return [0.5];
        }

        return this.children.map((child) => {
            return (child.relativeStartTime + child.duration / 2) / this.duration;
        });
    }

    /** Returns a list of time slices for each child timeline */
    getRelativeTimeRangesForChildren(): TimeSlice[] {
        return this.children.map((child) => {
            return [child.relativeStartTime / this.duration, child.relativeEndTime / this.duration];
        });
    }

    /**
     * Adds a callback we want to run whenever we reach a certain time. For each instance, we might want to know when we
     * start playing back our current Timeline. Here we would attach a callback at time 0. So each time we start playing
     * back the Timeline from the start or time 0, it would evaluate our callback.
     *
     * NOTE: this time is relative to _this_ timeline's time
     *
     * @param callback - the function we want to call when event happens
     * @param currentTime - the time we want to call our callback function
     * @param params - any parameters we to use when calling our callback function
     * @param scope - the scope we want to run the callback function within
     */
    addCallback(callback: Function, currentTime?: number, params?: Array<any>, scope?: object) {
        if (currentTime === undefined) {
            currentTime = this.duration;
        }
        if (params === undefined) {
            params = [];
        }
        if (scope === undefined) {
            scope = null;
        }
        this.callbacks.push({
            function: callback,
            scope,
            params,
            emitted: false,
            time: currentTime,
        });
    }

    /**
     * Run all callbacks when the current time matches the linked time during playback. These callback are mainly used to
     * start and stop playback from our HTML video and audio tags.
     */
    #runCallbacks(currentTime: number) {
        this.callbacks.forEach((c) => {
            // make sure we get to reset the emitted value. This is especially important for when
            // the time is 0 which is something the never happens during playback as it always jumps
            // a few milliseconds
            if (currentTime === c.time) {
                c.emitted = false;
            }
            // make sure we only trigger our callbacks during playback. This is assuming that these
            // callbacks are only being used to trigger the playback of elements like video or audio.
            if (this.playing()) {
                if (currentTime >= c.time && c.emitted === false) {
                    c.function.apply(c.scope, c.params);
                    c.emitted = true;
                }
                if (currentTime < c.time && c.emitted === true) {
                    c.emitted = false;
                }
            }
        });
    }

    removeCallback(callback: Function) {
        this.callbacks = this.callbacks.filter((c) => c.function !== callback);
    }

    /**
     * Get the current progress which is based on the position of current relative time.
     * We use this progress value mainly to drive our animation curves.
     */
    get progress(): number {
        return this.duration ? this.currentTime / this.duration : 0;
    }

    /**
     * Destroy the timeline and all of its children
     */
    destroy() {
        this.#destroyed = true;
        this.autoUpdate = false;
        if (this.parent) {
            this.parent.remove(this);
        }
        this.removeAllListeners();
        this.#duration = 0;
        this._relativeStartTime = 0;
        this.#currentTime = 0;
        this._emittedStart = false;
        this._emittedEnd = false;
        this.#parent = null;
        this.children.forEach((c) => c.destroy());
        this.children = [];
        this.callbacks = [];
    }
}
